/**
 * @license
 * Copyright (C) 2022 Anna Plodari
 * Software proprietario - Tutti i diritti riservati.
 * Registrato SIAE - Sezione OLAF.
 *
 * È vietato qualsiasi utilizzo non autorizzato, copia, modifica, estrazione,
 * riuso, rielaborazione, distribuzione, reverse engineering o decompilazione
 * del software, delle API, delle logiche applicative e dei dati del database.
 *
 * L'accesso al codice non costituisce concessione di licenza.
 */
/* eslint-disable react/function-component-definition */
import React, { useState } from 'react';
import * as yup from 'yup';
import { Control, FieldErrors, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import PrimaryButton from '../../components/PrimaryButton';
import SecondaryLink from '../../components/SecondaryLink';
import FormContext from '../../context/FormContext';
import useProfile from '../../context/ProfileContext';
import CheckboxGroup from '../../components/CheckboxGroup';
import * as schedaTecnicaOpts from '../../data/scheda-tecnica';
import { SchedaTecnica } from '../../types/SchedaTecnica';
import CheckboxTitle from '../../components/CheckboxTitle';
import InputLg from '../../components/InputLg';
import { checkTruthyOrNotEmpty, regexBlocked, validateExtraField } from '../../lib/utils/validation';
import Dialogue from '../../components/Dialogue';
import InputSm from '../../components/InputSm';
import Radio from '../../components/Radio';
import { BrowserExtra, CantieristicaNavaleExtra, CmsExtra, DatabaseExtra, DisegnoTecnicoExtra, EmailExtra, InputSchema, MontaggioExtra, NavigazioneInternetExtra, StrumentiAnalisiExtra, TipoVeicoliExtra } from './ExtraFieldsAndValidation';


export function VerniciatureExtra({ control, errors  }: { control: Control<any>, errors: any }) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.verniciatura.verniciatura' });

if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.verniciatura.extra"
        placeholder="Specifica il tipo di verniciatura industriale"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.verniciatura?.extra?.message}
      />
    );
  }

  return null;
}

export function DisegnoMeccanicoExtra({ control, errors }: { control: Control<any>, errors: any }) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.disegno_meccanico.tipo' });

  if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.disegno_meccanico.extra"
        placeholder="Specifica il tipo di disegno meccanico"
        className="pl-[21px] text-sm text-left text-textgrey"
        error={errors.scheda_tecnica?.disegno_meccanico?.extra?.message}
      />
    );
  }

  return null;
}

export function StrumentiExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.gestione_database.strumenti_gestione.tipo' });

if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.gestione_database.strumenti_gestione.extra"
        placeholder="Specifica il tipo di strumento di gestione"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.gestione_database?.strumenti_gestione?.extra?.message}
      />
    );
  }

  return null;
}

const Onboarding7: React.FC = () => {
  const profileContext = useProfile();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<{ scheda_tecnica: SchedaTecnica }>({
    defaultValues: { scheda_tecnica: profileContext?.data?.scheda_tecnica },
    resolver: yupResolver(InputSchema),
    mode: 'onChange',
  });

  

  const onSubmit: SubmitHandler<{ scheda_tecnica: SchedaTecnica }> = (data) => {
    if (checkTruthyOrNotEmpty(data.scheda_tecnica)) {
      profileContext.onDataUpdate(data);
      navigate('/valori-aziendali-ricercati');
    } else {
      setIsOpen(true);
    }
  };
console.log('err', errors);

  return (
    <FormContext.Provider value={{ errors, register }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col justify-between h-screen bg-onboardingmobile6 lg:bg-onboarding6 bg-no-repeat bg-right-top lg:bg-[right_-16rem_top] xl:bg-right-top bg-cover lg:bg-contain">
          <div className="p-6 flex justify-start">
            <img src="/logo.svg" alt="" className="max-h-[30px]" />
          </div>
          <div className="flex flex-col flex-grow px-6 lg:px-28 pt-8 lg:pt-2 max-w-5xl pb-4 lg:pb-0 overflow-y-scroll hide-scrollbar">
            <div>
              <h1 className="font-primarybold text-3xl text-primary">Scheda tecnica</h1>
              <p className="text-lg text-textgrey">Esperienze di lavoro</p>
              <p className="text-lg text-textgrey">
                (Nella scheda tecnica è obbligatorio flaggare almeno una opzione per poter procedere)
              </p>
            </div>
            {/* Disegno meccanico */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Disegno meccanico</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.DISEGNO_MECCANICO}
                formName="scheda_tecnica.disegno_meccanico.tipo"
                optClasses="flex items-center space-x-6">
                <DisegnoMeccanicoExtra control={control} errors={errors} />
              </CheckboxGroup>
            </div>
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Disegno tecnico</h2>
            </div>
            <div className="flex items-center space-x-6">
              <Radio label="Si" name="scheda_tecnica.disegno_tecnico.tipo" />
              <Radio label="No" name="scheda_tecnica.disegno_tecnico.tipo" />
              <DisegnoTecnicoExtra control={control} errors={errors} />
            </div>
            {/* Macchine utensili */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Macchine utensili</h2>
            </div>
            <div className="flex flex-col custom-checkbox-mobile-cont">
              <CheckboxGroup
                label="Centri lavoro CNC"
                opts={schedaTecnicaOpts.CENTRI_LAVORO_CNC}
                formName="scheda_tecnica.macchine_utensili.centri_lavoro_cnc"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
              <br />
              <CheckboxGroup
                label="Torni"
                opts={schedaTecnicaOpts.TORNI}
                formName="scheda_tecnica.macchine_utensili.torni.torni"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
              <div className="pl-8 mt-4">
                <p className="text-xs text-left font-primarybold text-textgrey">Numero assi</p>
                <CheckboxGroup
                  label=""
                  opts={schedaTecnicaOpts.NR_ASSI_TORNI}
                  formName="scheda_tecnica.macchine_utensili.torni.nr_assi_torni"
                />
              </div>
              <div className="pl-8 mt-4">
                <p className="text-xs text-left font-primarybold text-textgrey">Tipo di tornitura automatica</p>
                <CheckboxGroup
                  label=""
                  opts={schedaTecnicaOpts.TIPO_TORNITURA}
                  formName="scheda_tecnica.macchine_utensili.torni.tipo_tornitura"
                />
              </div>
              <br />
              <CheckboxGroup
                label="Fresatrici"
                opts={schedaTecnicaOpts.FRESATRICI}
                formName="scheda_tecnica.macchine_utensili.fresatrici.fresatrici"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
              <div className="pl-8 mt-4">
                <p className="text-xs text-left font-primarybold text-textgrey">Frese in base alla lavorazione</p>
                <CheckboxGroup
                  label=""
                  opts={schedaTecnicaOpts.TIPI_FRESE}
                  formName="scheda_tecnica.macchine_utensili.fresatrici.tipi_frese"
                />
              </div>
              <div className="pl-8 mt-4">
                <p className="text-xs text-left font-primarybold text-textgrey">Numero assi</p>
                <CheckboxGroup
                  label=""
                  opts={schedaTecnicaOpts.NR_ASSI_FRESE}
                  formName="scheda_tecnica.macchine_utensili.fresatrici.nr_assi_frese"
                />
              </div>
              <br />
              <CheckboxTitle name="scheda_tecnica.macchine_utensili.trapani_colonna" label="Trapani a colonna" />
              <CheckboxTitle name="scheda_tecnica.macchine_utensili.macchine_transfer" label="Macchine transfer" />
              <CheckboxTitle
                name="scheda_tecnica.macchine_utensili.macchine_maschiatrici"
                label="Macchine maschiatrici"
              />
              <CheckboxTitle name="scheda_tecnica.macchine_utensili.presse_piegatrici" label="Presse piegatrici" />
              <CheckboxTitle
                name="scheda_tecnica.macchine_utensili.taglio_lamiera"
                label="Macchine per il taglio lamiera"
              />
              <InputLg
                name="scheda_tecnica.macchine_utensili.extra"
                placeholder="Inserire altre macchine utensili"
                className="pl-[21px] pt-2 text-sm text-left text-textgrey"
                error={errors.scheda_tecnica?.macchine_utensili?.extra?.message}
              />
            </div>
            {/* Rettifica */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Rettifica</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.RETTIFICA}
                formName="scheda_tecnica.rettifica"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Saldatura */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Saldatura</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.SALDATURA}
                formName="scheda_tecnica.saldatura.tipi"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <InputLg
              name="scheda_tecnica.saldatura.extra"
              placeholder="Inserire altri tipi di saldatura"
              className="pl-[21px] pt-2 text-sm text-left text-textgrey"
              error={errors.scheda_tecnica?.saldatura?.extra?.message}
            />
            {/* Trattamenti galvanici nel settore industriale */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Trattamenti galvanici nel settore industriale</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.TRATTAMENTI_GALVANICI}
                formName="scheda_tecnica.trattamenti_galvanici"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Stampaggio industriale */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Stampaggio industriale</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.STAMPAGGIO_INDUSTRIALE}
                formName="scheda_tecnica.stampaggio.stampaggio"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Macchine da stampa</p>
              <CheckboxGroup
                label=""
                opts={['offset', 'rotative', 'digitali']}
                formName="scheda_tecnica.stampaggio.macchine"
              />
            </div>
            {/* Assemblaggio */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Assemblaggio</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.ASSEMBLAGGIO}
                formName="scheda_tecnica.assemblaggio"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Montaggio */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Montaggio</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.MONTAGGIO}
                formName="scheda_tecnica.montaggio"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Linguaggi di programmazione per PLC */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Linguaggi di programmazione per PLC</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.LINGUAGGI_PLC}
                formName="scheda_tecnica.linguaggi_programmazione_plc"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Sistema CNC */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Sistema CNC</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.SISTEMA_CNC}
                formName="scheda_tecnica.sistema_cnc.sistemi"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <InputLg
              name="scheda_tecnica.sistema_cnc.extra"
              placeholder="Inserire altri sistemi CNC"
              className="pl-[21px] pt-2 text-sm text-left text-textgrey"
              error={errors.scheda_tecnica?.sistema_cnc?.extra?.message}
            />
            {/* Ingegneria */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Ingegneria</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.INGEGNERIE}
                formName="scheda_tecnica.ingegneria.tipo"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <InputLg
              name="scheda_tecnica.ingegneria.extra"
              placeholder="Inserire altre tipologie"
              className="pl-[21px] pt-2 text-sm text-left text-textgrey"
              error={errors.scheda_tecnica?.ingegneria?.extra?.message}
            />
            {/* Linguaggi di programmazione */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Linguaggi di programmazione</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.LINGUAGGI}
                formName="scheda_tecnica.linguaggi_programmazione.linguaggi"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <InputLg
              name="scheda_tecnica.linguaggi_programmazione.extra"
              placeholder="Inserire altri linguaggi di programmazione"
              className="pl-[21px] pt-2 text-sm text-left text-textgrey"
              error={errors.scheda_tecnica?.linguaggi_programmazione?.extra?.message}
            />
            {/* Linguaggio di progettazione */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Linguaggi di progettazione</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.SOFTWARE_PROGETTAZIONE}
                formName="scheda_tecnica.linguaggi_progettazione.linguaggi"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <InputLg
              name="scheda_tecnica.linguaggi_progettazione.extra"
              placeholder="Inserire altri linguaggi di progettazione"
              className="pl-[21px] pt-2 text-sm text-left text-textgrey"
              error={errors.scheda_tecnica?.linguaggi_progettazione?.extra?.message}
            />
            {/* Competenze Informatiche */}
            <div className="mt-6 mb-2">
              <h2 className="font-primarybold text-lg text-black">Competenze Informatiche</h2>
              <h5 className="text-sm text-left font-primarybold text-textgrey mt-2">Sistemi Operativi</h5>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.SISTEMI_OPERATIVI}
                formName="scheda_tecnica.sistemi_operativi"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <br />
            {/* Software per la Produttività */}

            <label className="text-sm text-left font-primarybold text-textgrey">Software per la Produttività</label>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Microsoft Office Suite</p>
              <CheckboxGroup
                label=""
                opts={['Word', 'Excel', 'PowerPoint', 'Outlook']}
                formName="scheda_tecnica.software_produttivita.microsoft"
              />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Google Workspace</p>
              <CheckboxGroup
                label=""
                opts={['Documenti', 'Fogli', 'Presentazioni', 'Gmail']}
                formName="scheda_tecnica.software_produttivita.google"
              />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Gestione di fogli di calcolo</p>
              <CheckboxGroup
                label=""
                opts={['Formule', 'Funzioni', 'Grafici']}
                formName="scheda_tecnica.software_produttivita.fogli_calcolo"
              />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Creazioni di presentazioni</p>
              <CheckboxGroup
                label=""
                opts={['Presentazioni']}
                formName="scheda_tecnica.software_produttivita.fogli_calcolo"
              />
            </div>
            <br />
            {/*  Navigazione internet */}

            <label className="text-sm text-left font-primarybold text-textgrey">Navigazione in Internet</label>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Motori di ricerca</p>
              <CheckboxGroup
                label=""
                opts={['Google', 'Bing', 'Yahoo', 'altro']}
                formName="scheda_tecnica.navigazione_internet.motori_ricerca.tipo"
              />
              <NavigazioneInternetExtra control={control} errors={errors} />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Browser</p>
              <CheckboxGroup
                label=""
                opts={['Chrome', 'Firefox', 'Safari', 'Explorer', 'Altro']}
                formName="scheda_tecnica.navigazione_internet.browser.tipo"
              />
              <BrowserExtra control={control} errors={errors} />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Utilizzo di email</p>
              <CheckboxGroup
                label=""
                opts={['Gmail', 'Outlook', 'Altro']}
                formName="scheda_tecnica.navigazione_internet.email.tipo"
              />
              <EmailExtra control={control} errors={errors} />
            </div>
            {/* Strumenti di Comunicazione */}
            <div className="mt-6 mb-2">
              <h5 className="text-sm text-left font-primarybold text-textgrey mt-2">Strumenti di Comunicazione</h5>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.STRUMENTI_COMUNICAZIONE}
                formName="scheda_tecnica.strumenti_comunicazione"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <br />
            {/*  Gestione di Database */}
            <label className="text-sm text-left font-primarybold text-textgrey">Gestione di Database</label>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Database relazionali</p>
              <CheckboxGroup
                label=""
                opts={['MySQL', 'PostgreSQL', 'altro']}
                formName="scheda_tecnica.gestione_database.db_relazionali.tipo"
              />
              <DatabaseExtra control={control} errors={errors} />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Strumenti di gestione</p>
              <CheckboxGroup
                label=""
                opts={['Access', 'Altro']}
                formName="scheda_tecnica.gestione_database.strumenti_gestione.tipo"
              />
              <StrumentiExtra control={control} errors={errors} />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Database no-relazionali</p>
              <CheckboxGroup label="" opts={['NoSQL']} formName="scheda_tecnica.gestione_database.db_no_relazionali" />
            </div>
            <br />
            {/*  Grafica e Multimedia */}
            <CheckboxGroup
              label="Grafica e Multimedia"
              opts={['Adobe Photoshop', 'Adobe Illustrator', 'Adobe InDesign', 'Adobe Premiere Pro']}
              formName="scheda_tecnica.grafica_multimedia.grafica_multimedia"
              optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12 lg:w-2/3"
            />
            <div className="mt-2">
              <p className="text-xs text-left font-primarybold text-textgrey">Montaggio video</p>
              <CheckboxGroup
                label=""
                opts={['Final Cut Pro', 'altro']}
                formName="scheda_tecnica.grafica_multimedia.montaggio_video.tipo"
              />
              <MontaggioExtra control={control} errors={errors} />
            </div>
            <br />
            {/*  Sviluppo Web */}
            <CheckboxGroup
              label="Sviluppo Web"
              opts={['HTML5 & CSS3', 'JavaScript', 'Ajax', 'JQuery']}
              formName="scheda_tecnica.sviluppo_web.sviluppo_web"
              optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12 lg:w-2/3"
            />
            <div className="mt-2">
              <p className="text-xs text-left font-primarybold text-textgrey">CMS</p>
              <CheckboxGroup
                label=""
                opts={['WordPress', 'Drupal', 'Joomla', 'Altro']}
                formName="scheda_tecnica.sviluppo_web.cms.tipo"
              />
              <CmsExtra control={control} errors={errors} />
            </div>
            <br />
            {/*  Analisi dei Dati */}
            <label className="text-sm text-left font-primarybold text-textgrey">Analisi dei Dati</label>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Strumenti di analisi</p>
              <CheckboxGroup
                label=""
                opts={['Tableau', 'Power BI', 'Altro']}
                formName="scheda_tecnica.analisi_dati.strumenti_analisi.tipo"
              />
              <StrumentiAnalisiExtra control={control} errors={errors} />
            </div>
            <div className="mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Linguaggi per l&apos;analisi</p>
              <CheckboxGroup label="" opts={['R', 'Python']} formName="scheda_tecnica.analisi_dati.linguaggi_analisi" />
            </div>
            {/* Sicurezza informatica */}
            <div className="mt-6 mb-2">
              <h5 className="text-sm text-left font-primarybold text-textgrey mt-2">Sicurezza Informatica</h5>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.SICUREZZA_INFORMATICA}
                formName="scheda_tecnica.sicurezza_informatica"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Intelligenza Artificiale */}
            <div className="mt-6 mb-2">
              <h5 className="text-sm text-left font-primarybold text-textgrey mt-2">Intelligenza Artificiale</h5>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:flex-col gap-x-4 gap-y-4 md:md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.INTELLIGENZA_ARTIFICIALE}
                formName="scheda_tecnica.intelligenza_artificiale.intelligenza_artificiale"
                optClasses="lg:grid lg:grid-cols-3 gap-1 lg:gap-x-12"
              />

              <InputLg
                name="scheda_tecnica.intelligenza_artificiale.extra"
                placeholder="Altro"
                className="pl-[21px] pt-2 text-sm text-left text-textgrey"
                error={errors.scheda_tecnica?.intelligenza_artificiale?.extra?.message}
              />
            </div>
            {/* Sistemi meccatronici */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Sistemi meccatronici</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.SISTEMI_MECCATRONICI}
                formName="scheda_tecnica.sistemi_meccatronici.sistemi"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            <InputLg
              name="scheda_tecnica.sistemi_meccatronici.extra"
              placeholder="Altro"
              className="pl-[21px] pt-2 text-sm text-left text-textgrey"
              error={errors.scheda_tecnica?.sistemi_meccatronici?.extra?.message}
            />

            {/* Altri macchinari industriali */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Altri macchinari industriali</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.ALTRI_MACCHINARI_INDUSTRIALI}
                formName="scheda_tecnica.altri_macchinari_industriali"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Strumenti di precisione */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Strumenti di precisione</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.STRUMENTI_PRECISIONE}
                formName="scheda_tecnica.strumenti_precisione"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Apparecchiature di produzione LCD e semiconduttori */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">
                Apparecchiature di produzione LCD e semiconduttori
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.PRODUZIONE_LCD_SEMICONDUTTORI}
                formName="scheda_tecnica.apparecchiature_produzione_lcd_semi"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Robot industriali */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Robot industriali</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.ROBOT}
                formName="scheda_tecnica.robot_industriali"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/* Dispositivi elettronici */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Dispositivi elettronici</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.DISPOSITIVI_ELETTRONICI}
                formName="scheda_tecnica.dispositivi_elettronici"
                optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12"
              />
            </div>
            {/*  Impianti elettrici */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Impianti elettrici</h2>
            </div>
            <CheckboxGroup
              label=" "
              opts={['Civili', 'Industriali', 'Cantieri edili']}
              formName="scheda_tecnica.impianti_elettrici.impianti_elettrici"
              optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12 lg:w-2/3"
            />
            <div className="pl-8 mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Competenze specifiche</p>
              <CheckboxGroup
                label=""
                opts={['progettazione', 'realizzazione', 'manutenzione e controllo']}
                formName="scheda_tecnica.impianti_elettrici.competenze"
              />
            </div>
            <div className="pl-8 mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Attestato</p>
              <CheckboxGroup
                label=""
                opts={['PES', 'PAV', 'PEI']}
                formName="scheda_tecnica.impianti_elettrici.attestato"
              />
            </div>
            {/*  Autotrasporto */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Autotrasporto</h2>
            </div>
            <CheckboxGroup
              label=" "
              opts={['Merci', 'Persone']}
              formName="scheda_tecnica.autotrasporto.autotrasporto"
              optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12 lg:w-2/3"
            />
            <InputSm
              name="scheda_tecnica.autotrasporto.tipo_patente"
              placeholder="Tipo di patente"
              className="pt-2 text-sm mt-2 text-textgrey"
              error={errors.scheda_tecnica?.autotrasporto?.tipo_patente?.message}
            />
            <InputSm
              name="scheda_tecnica.autotrasporto.tipo_merci"
              placeholder="Tipologia merci"
              className=" pt-2 text-sm  text-textgrey"
              error={errors.scheda_tecnica?.autotrasporto?.tipo_merci?.message}
            />
            <div className=" mt-4">
              <p className="text-xs font-primarybold text-textgrey">Tipologia di veicoli utilizzati</p>
              <CheckboxGroup
                label=""
                opts={[
                  'autocarri',
                  'autotreni',
                  'autoarticolati/bilici',
                  'furgoni',
                  'camion frigoriferi',
                  'camion a pianale',
                  'betoniere',
                  'altro',
                ]}
                formName="scheda_tecnica.autotrasporto.tipo_veicoli.tipo_veicoli.tipo"
                optClasses="lg:grid lg:grid-cols-4 gap-1 lg:gap-x-12 mt-2"
              />
              <TipoVeicoliExtra control={control} errors={errors} />
            </div>
            <div className="pl-8 mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Categoria Autocarri</p>
              <CheckboxGroup
                label=""
                opts={['N1', 'N2', 'N3']}
                formName="scheda_tecnica.autotrasporto.tipo_veicoli.categoria"
              />
            </div>
            {/*  Cantieristica navale */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Cantieristica navale</h2>
            </div>
            <CheckboxGroup
              label=" "
              opts={[
                'costruttore navale',
                'carpentiere navale',
                'capo barca',
                'montatore',
                'meccanico',
                'elettricista',
                'idraulico',
                'laminatore',
                'falegname nautico',
                'allestitore nautico',
                'altro',
              ]}
              formName="scheda_tecnica.cantieristica_navale.cantieristica_navale.tipo"
              optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12 lg:w-2/3"
            />
            <CantieristicaNavaleExtra control={control} errors={errors} />
            <div className="pl-8 mt-4">
              <p className="text-xs font-primarybold text-textgrey">Tipologia</p>
              <CheckboxGroup
                label=""
                opts={['natanti', 'imbarcazioni', 'navi']}
                formName="scheda_tecnica.cantieristica_navale.tipologia"
                optClasses="lg:grid lg:grid-cols-4 gap-1 lg:gap-x-12 mt-2"
              />
            </div>
            <div className="pl-8 mt-4">
              <p className="text-xs text-left font-primarybold text-textgrey">Classificazioni</p>
              <CheckboxGroup
                label=""
                opts={['s motore', 'a vela', 'motorsailer']}
                formName="scheda_tecnica.cantieristica_navale.classificazione"
              />
            </div>
            {/*  Officina meccatronica */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Officina meccatronica delle riparazioni</h2>
            </div>
            <CheckboxGroup
              label=" "
              opts={['Auto', 'Elettrauto', 'Meccanico motorista', 'Veicoli industriali/mezzi pesanti']}
              formName="scheda_tecnica.meccatronica_officina.meccatronica_officina"
              optClasses="lg:grid lg:grid-cols-2 gap-1 lg:gap-x-12 lg:w-2/3"
            />

            <div className="pl-8 mt-4">
              <p className="text-xs font-primarybold text-textgrey">Competenze specifiche</p>
              <CheckboxGroup
                label=""
                opts={[
                  'manutenzione, riparazione e/o sostituzione degli apparati elettrico/elettronici del veicolo',
                  'manutenzione, riparazione e/o sostituzione di parti meccaniche dell’autoveicolo',
                  'diagnosi tecnica e strumentale degli apparati elettrico/elettronici del veicolo',
                  'diagnosi tecnica e strumentale delle parti meccaniche del veicolo',
                  'gestione attività di una oﬃcina di autoriparazione',
                ]}
                formName="scheda_tecnica.meccatronica_officina.competenze"
                optClasses="lg:grid lg:grid-cols-1 gap-1 lg:gap-x-12 mt-2"
              />
            </div>
            {/* Verniciatura industriale */}
            <div className="mt-6 mb-4">
              <h2 className="font-primarybold text-lg text-black">Verniciatura industriale</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 md:gap-x-6 w-fit">
              <CheckboxGroup
                opts={schedaTecnicaOpts.VERNICIATUA_INDUSTRIALE}
                formName="scheda_tecnica.verniciatura.verniciatura"
                optClasses="lg:grid lg:grid-cols-3 gap-1 lg:gap-x-12 mt-2">
                <VerniciatureExtra control={control} errors={{ errors }} />
              </CheckboxGroup>
            </div>
          </div>
          <div className="flex flex-col justify-center px-3 lg:px-28 pt-8 pb-8 relative bg-white lg:bg-transparent border-t border-t-gray-100 lg:border-t-0">
            <div className="flex justify-between items-center mobile-button-sizing">
              <SecondaryLink text="Indietro" to="/mansioni" />
              <PrimaryButton text="Avanti" />
            </div>
          </div>
        </div>
      </form>
      <Dialogue
        title="Scheda tecnica non compilata"
        message="Devi esprimere almeno una scelta per poter continuare!"
        primaryTextButton="Ho capito"
        isOpen={isOpen}
        closeFunction={() => {
          setIsOpen(false);
        }}
      />
    </FormContext.Provider>
  );
};

export default Onboarding7;